import {
  Button,
  Container,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { newDataLayer, registerDataLayer } from 'src/utilities/google-tools';
import { useHistory, useLocation } from 'react-router-dom';
import {
  validateCompanyInfo,
  validatePersonalInfo,
  validateUserCompanyWorkingHours,
} from '../../utilities/validations/registration-validations';
import { Box, padding } from '@mui/system';
import CircularProgress from '@mui/material/CircularProgress';
import { checkToken } from '../Products/api';
import isEmpty from 'is-empty';
import { registerUserWithLink } from '../../api/api-functions';
import { getTimeStringFromDateString } from '../../utilities/work-time-parser';
import CustomTimePicker from '../../components/inputs/CustomTimePicker';

const COMPANY_OWNER_NOT_SELECTED = -1;

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    [theme.breakpoints.down('xs')]: {
      padding: 0,
      justifyContent: 'flex-start',
      backgroundColor: '#ffffff',
    },
  },
  paper: {
    width: '100%',
    maxWidth: '700px',
    padding: theme.spacing(5),
    borderRadius: '16px',
    boxShadow: '0px 6px 16px 0px rgba(0,0,0,0.08)',
    backgroundColor: '#ffffff',
    [theme.breakpoints.down('xs')]: {
      maxWidth: '100%',
      boxShadow: 'none',
      borderRadius: 0,
      padding: theme.spacing(3),
    },
  },
  heading: {
    fontWeight: 700,
    color: '#111827',
    marginBottom: theme.spacing(3),
  },
  subHeading: {
    fontWeight: 600,
    color: '#374151',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      '& fieldset': {
        borderColor: '#D1D5DB',
      },
      '&:hover fieldset': {
        borderColor: '#9CA3AF',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#40b4e5',
        borderWidth: '2px',
      },
    },
    '& .MuiInputLabel-root': {
      color: '#6B7280',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#40b4e5',
    },
    '& .MuiInputBase-input': {
      padding: 16,
    },
  },
  submitButton: {
    backgroundColor: '#fff',
    color: '#40b4e5',
    borderColor: '#40b4e5',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '16px',
    borderRadius: '8px',
    padding: '12px 0',
  },
  switchBase: {
    color: '#E5E7EB',
    '&$checked': {
      color: '#40b4e5',
    },
    '&$checked + $track': {
      backgroundColor: '#40b4e5',
      opacity: 0.5,
    },
  },
  checked: {},
  track: {},
}));

function RegistrationWithLink() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    emailAddress: '',
    phoneNumber: '',
    username: '',
    clientId: -1,
    companyName: '',
    companyAddress: '',
    companyTypeOfBusiness: '',
    ceoFirstName: '',
    ceoLastName: '',
    ceoPhoneNumber: '',
    ceoEmail: '',
    createdByID: -1,
    adminClientCompanyID: -1,
    token: '',
    workDayMonday: false,
    workDayTuesday: false,
    workDayWednesday: false,
    workDayThursday: false,
    workDayFriday: false,
    workDaySaturday: false,
    workDaySunday: false,
    workDayMondayFrom: null,
    workDayMondayTo: null,
    workDayTuesdayFrom: null,
    workDayTuesdayTo: null,
    workDayWednesdayFrom: null,
    workDayWednesdayTo: null,
    workDayThursdayFrom: null,
    workDayThursdayTo: null,
    workDayFridayFrom: null,
    workDayFridayTo: null,
    workDaySaturdayFrom: null,
    workDaySaturdayTo: null,
    workDaySundayFrom: null,
    workDaySundayTo: null,
    adminClientOwnerId: COMPANY_OWNER_NOT_SELECTED,
    acceptAgreement: false,
    role: 0,
    errors: {},
    loading: true,
  });

  const worktimeActiveHintStyle = '#111827';
  const location = useLocation();
  let history = useHistory();
  const query = new URLSearchParams(location.search);
  const cleanToken = query.get('token');

  useEffect(() => {
    (async () => {
      try {
        const res = await checkToken(cleanToken);
        if (!res.Success) {
          enqueueSnackbar(res.Message || 'This invitation link is invalid or has expired.', { variant: 'error' });
          setFormData({ ...formData, loading: false });
          return;
        }

        setFormData({
          ...formData,
          emailAddress: res.Result.email,
          clientId: res.Result.clientID,
          createdByID: res.Result.createdByID,
          adminClientCompanyID: res.Result.adminClientCompanyID,
          role: res.Result.role,
          token: cleanToken,
          loading: false,
        });
      } catch (error) {
        enqueueSnackbar(error?.message || 'An unexpected error occurred.', { variant: 'error' });
        setFormData((prev) => ({ ...prev, loading: false }));
      }
    })();
  }, []);

  const handleChange = (event) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const onToggle = (e, iv) => {
    setFormData({ ...formData, [e.target.name]: iv });
  };

  const handleSubmit = () => {
    setFormData({ ...formData, message: '', snackbarOppen: false });
    validate(formData.role).then(({ valid }) => {
      if (valid) {
        setFormData({ ...formData, errors: {} });
        registerUserWithLink({
          AdminClientCompanyId: formData.adminClientCompanyID,
          CreatedByID: formData.createdByID,
          Token: formData.token,
          User_UserTypeID: formData.role,
          User_Username: formData.username,
          User_FirstName: formData.firstname,
          User_LastName: formData.lastname,
          User_PhoneNumber: formData.phoneNumber,
          User_Email: formData.emailAddress,
          Guest_ClientId: formData.clientId,
          AdminClient_UserId:
            formData.adminClientOwnerId === COMPANY_OWNER_NOT_SELECTED
              ? null
              : formData.adminClientOwnerId,
          Company_Name: formData.companyName,
          Company_Address: formData.companyAddress,
          Company_TypeOfBusiness: formData.companyTypeOfBusiness,
          CEO_FirstName: formData.ceoFirstName,
          CEO_LastName: formData.ceoLastName,
          CEO_PhoneNumber: formData.ceoPhoneNumber,
          CEO_Email: formData.ceoEmail,
          WorkDay_Monday: formData.workDayMonday,
          WorkDay_Tuesday: formData.workDayTuesday,
          WorkDay_Wednesday: formData.workDayWednesday,
          WorkDay_Thursday: formData.workDayThursday,
          WorkDay_Friday: formData.workDayFriday,
          WorkDay_Saturday: formData.workDaySaturday,
          WorkDay_Sunday: formData.workDaySunday,
          WorkHours_Monday_From: getTimeStringFromDateString(
            formData.workDayMondayFrom
          ),
          WorkHours_Monday_To: getTimeStringFromDateString(
            formData.workDayMondayTo
          ),
          WorkHours_Tuesday_From: getTimeStringFromDateString(
            formData.workDayTuesdayFrom
          ),
          WorkHours_Tuesday_To: getTimeStringFromDateString(
            formData.workDayTuesdayTo
          ),
          WorkHours_Wednesday_From: getTimeStringFromDateString(
            formData.workDayWednesdayFrom
          ),
          WorkHours_Wednesday_To: getTimeStringFromDateString(
            formData.workDayWednesdayTo
          ),
          WorkHours_Thursday_From: getTimeStringFromDateString(
            formData.workDayThursdayFrom
          ),
          WorkHours_Thursday_To: getTimeStringFromDateString(
            formData.workDayThursdayTo
          ),
          WorkHours_Friday_From: getTimeStringFromDateString(
            formData.workDayFridayFrom
          ),
          WorkHours_Friday_To: getTimeStringFromDateString(
            formData.workDayFridayTo
          ),
          WorkHours_Saturday_From: getTimeStringFromDateString(
            formData.workDaySaturdayFrom
          ),
          WorkHours_Saturday_To: getTimeStringFromDateString(
            formData.workDaySaturdayTo
          ),
          WorkHours_Sunday_From: getTimeStringFromDateString(
            formData.workDaySundayFrom
          ),
          WorkHours_Sunday_To: getTimeStringFromDateString(
            formData.workDaySundayTo
          ),
        })
          .then(async (data) => {
            if (data.StatusCode === 400) {
              enqueueSnackbar(data.Message || 'An error occurred. Please try again.', { variant: 'error' });
              setFormData((prev) => ({ ...prev, loading: false }));
            } else {
              enqueueSnackbar(data.Message || 'Registration successful!', { variant: 'success' });
              setFormData((prev) => ({ ...prev, loading: false }));
              setTimeout(() => {
                history.push('/login');
              }, 2500);
            }
          })
          .catch((error) => {
            enqueueSnackbar(error?.message || 'An error occurred. Please try again.', { variant: 'error' });
            setFormData((prev) => ({ ...prev, loading: false }));
          });
      }
    });
  };

  const validate = async (role) => {
    var errorsFinal;
    var result;
    var valid;

    const validateUser = [2, 3, 4, 5];
    const validateCompany = [3];
    const validateWorkingHours = [3];

    if (validateUser.includes(role)) {
      result = await validatePersonalInfo(formData, false, false, true);
      if (!result.valid) {
        errorsFinal = Object.assign(result.errors, errorsFinal);
      }
    }

    if (validateCompany.includes(role)) {
      result = validateCompanyInfo(formData);
      if (!result.valid) {
        errorsFinal = Object.assign(result.errors, errorsFinal);
      }
    }

    if (validateWorkingHours.includes(role)) {
      result = validateUserCompanyWorkingHours(formData);
      if (!result.valid) {
        errorsFinal = Object.assign(result.errors, errorsFinal);
      }
    }

    valid = isEmpty(errorsFinal);
    if (!valid) {
      setFormData({ ...formData, errors: errorsFinal });
    }

    return { valid };
  };

  registerDataLayer(newDataLayer('Registration With Link'));

  const renderWorkDay = (dayName, keyName) => (
    <Grid container spacing={2} alignItems="center" style={{ marginBottom: 8 }}>
      <Grid item xs={12} sm={4}>
        <FormControlLabel
          control={
            <Switch
              classes={{
                switchBase: classes.switchBase,
                checked: classes.checked,
                track: classes.track,
              }}
              name={`workDay${keyName}`}
              value={formData[`workDay${keyName}`]}
              checked={formData[`workDay${keyName}`]}
              onChange={onToggle}
            />
          }
          label={
            <Typography style={{ fontFamily: "'Inter', sans-serif" }}>
              {dayName}
            </Typography>
          }
          labelPlacement="start"
          style={{ marginLeft: 0, width: '100%', justifyContent: 'space-between' }}
        />
      </Grid>
      <Grid item xs={6} sm={4}>
        <CustomTimePicker
          className={classes.textField}
          name={`workDay${keyName}From`}
          placeholder="Start"
          value={formData[`workDay${keyName}From`]}
          disabled={!formData[`workDay${keyName}`]}
          hintStyle={
            formData[`workDay${keyName}`]
              ? { color: worktimeActiveHintStyle }
              : {}
          }
          helperText={formData.errors?.[`workDay${keyName}From`]}
          error={formData.errors?.[`workDay${keyName}From`]}
          onTimeChange={onToggle}
          fullWidth
          InputProps={{
            classes: { root: classes.textField }
          }}
        />
      </Grid>
      <Grid item xs={6} sm={4}>
        <CustomTimePicker
          className={classes.textField}
          name={`workDay${keyName}To`}
          value={formData[`workDay${keyName}To`]}
          placeholder="End"
          disabled={!formData[`workDay${keyName}`]}
          hintStyle={
            formData[`workDay${keyName}`]
              ? { color: worktimeActiveHintStyle }
              : {}
          }
          helperText={formData.errors?.[`workDay${keyName}To`]}
          error={formData.errors?.[`workDay${keyName}To`]}
          onTimeChange={onToggle}
          fullWidth
          InputProps={{
            classes: { root: classes.textField }
          }}
        />
      </Grid>
    </Grid>
  );

  return !formData.loading ? (
    <Container className={classes.mainContainer} maxWidth={false}>
      <form style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        {formData.role ? (
          <Paper className={classes.paper}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h5" align="center" className={classes.heading}>
                  User Information
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  className={classes.textField}
                  helperText={formData.errors?.firstname || "Required *"}
                  name="firstname"
                  label="First name"
                  placeholder="First name"
                  value={formData.firstname}
                  error={!!formData.errors?.firstname}
                  onChange={handleChange}
                  size="small"
                  variant="outlined"
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  className={classes.textField}
                  name="lastname"
                  label="Last name"
                  placeholder="Last name"
                  value={formData.lastname}
                  error={!!formData.errors?.lastname}
                  helperText={formData.errors?.lastname || "Required *"}
                  onChange={handleChange}
                  size="small"
                  variant="outlined"
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  className={classes.textField}
                  name="phoneNumber"
                  label="Phone Number"
                  type="number"
                  placeholder="Phone number"
                  value={formData.phoneNumber}
                  error={!!formData.errors?.phoneNumber}
                  helperText={formData.errors?.phoneNumber || "Required *"}
                  onChange={handleChange}
                  size="small"
                  variant="outlined"
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  className={classes.textField}
                  name="username"
                  label="Username"
                  placeholder="Username"
                  value={formData.username}
                  error={!!formData.errors?.username}
                  helperText={formData.errors?.username || "Required *"}
                  onChange={handleChange}
                  size="small"
                  variant="outlined"
                  fullWidth
                />
              </Grid>

              {formData.role === 3 && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" align="center" className={classes.subHeading}>
                      Company Information
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      className={classes.textField}
                      name="companyName"
                      label="Company name"
                      placeholder="Company name"
                      value={formData.companyName}
                      error={!!formData.errors?.companyName}
                      helperText={formData.errors?.companyName}
                      onChange={handleChange}
                      size="small"
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      className={classes.textField}
                      name="companyAddress"
                      label="Company address"
                      placeholder="Company address"
                      value={formData.companyAddress}
                      error={!!formData.errors?.companyAddress}
                      helperText={formData.errors?.companyAddress}
                      onChange={handleChange}
                      size="small"
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      className={classes.textField}
                      name="companyTypeOfBusiness"
                      label="Type of Business"
                      placeholder="Company type of business"
                      multiline
                      minRows={2}
                      maxRows={6}
                      value={formData.companyTypeOfBusiness}
                      error={!!formData.errors?.companyTypeOfBusiness}
                      helperText={formData.errors?.companyTypeOfBusiness}
                      onChange={handleChange}
                      size="small"
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      className={classes.textField}
                      name="ceoFirstName"
                      label="CEO First Name"
                      placeholder="CEO First Name"
                      value={formData.ceoFirstName}
                      error={!!formData.errors?.ceoFirstName}
                      helperText={formData.errors?.ceoFirstName}
                      onChange={handleChange}
                      size="small"
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      className={classes.textField}
                      name="ceoLastName"
                      label="CEO Last Name"
                      placeholder="CEO Last Name"
                      value={formData.ceoLastName}
                      error={!!formData.errors?.ceoLastName}
                      helperText={formData.errors?.ceoLastName}
                      onChange={handleChange}
                      size="small"
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      className={classes.textField}
                      name="ceoPhoneNumber"
                      label="CEO Phone Number"
                      placeholder="CEO Phone Number"
                      value={formData.ceoPhoneNumber}
                      error={!!formData.errors?.ceoPhoneNumber}
                      helperText={formData.errors?.ceoPhoneNumber}
                      onChange={handleChange}
                      size="small"
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      className={classes.textField}
                      name="ceoEmail"
                      label="CEO Email Address"
                      placeholder="CEO Email Address"
                      value={formData.ceoEmail}
                      error={!!formData.errors?.ceoEmail}
                      helperText={formData.errors?.ceoEmail}
                      onChange={handleChange}
                      size="small"
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" align="center" className={classes.subHeading}>
                      Working Days
                    </Typography>
                    <Box mt={2}>
                      {renderWorkDay('Monday', 'Monday')}
                      {renderWorkDay('Tuesday', 'Tuesday')}
                      {renderWorkDay('Wednesday', 'Wednesday')}
                      {renderWorkDay('Thursday', 'Thursday')}
                      {renderWorkDay('Friday', 'Friday')}
                      {renderWorkDay('Saturday', 'Saturday')}
                      {renderWorkDay('Sunday', 'Sunday')}
                    </Box>
                  </Grid>
                </>
              )}

              <Grid item xs={12} style={{ marginTop: '20px', textAlign: 'center' }}>
                <Button
                  className={classes.submitButton}
                  variant="outlined"
                  onClick={handleSubmit}
                  type="button"
                  size="large"
                  fullWidth
                  style={{ maxWidth: '300px' }}
                >
                  Create
                </Button>
              </Grid>
            </Grid>
          </Paper>
        ) : null}

      </form>
    </Container>
  ) : (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
      }}
    >
      <CircularProgress style={{ color: '#40b4e5' }} />
    </Box>
  );
}

export default RegistrationWithLink;